<template>
  <div class="share-model">
    <div class="share-model-container">
      <div class="share-model-header">
        <div class="share-model-header-title">
          <p>{{ $t("shareModel.title") }}</p>
        </div>
        <div class="share-model-header-actions">
          <img src="@/assets/icons/xclose.svg" alt="close" @click="close" />
        </div>
      </div>

      <div class="share-model-access">
        <p class="share-model-access-title">
          {{ $t("shareModel.accessTitle") }}
        </p>
        <label
          v-for="option in accessOptions"
          :key="option.value"
          class="share-model-access-option"
          :class="{ 'share-model-access-option--selected': accessLevel === option.value }"
        >
          <input type="radio" :value="option.value" v-model="accessLevel" />
          <span class="share-model-access-option-text">
            <span class="share-model-access-option-label">{{
              $t(option.labelKey)
            }}</span>
            <span class="share-model-access-option-hint">{{
              $t(option.hintKey)
            }}</span>
          </span>
        </label>
      </div>

      <div class="share-by-email">
        <div class="share-by-email-container">
          <div class="share-by-email-title">
            <label for="share-model-email">{{
              $t("imageNavigation.emailAddress")
            }}</label>
            <input
              id="share-model-email"
              type="email"
              v-model="email"
              :placeholder="$t('imageNavigation.emailAddress')"
            />
          </div>
        </div>
        <div class="share-by-email-text">
          <label for="share-model-message">{{
            $t("imageNavigation.attachMessage")
          }}</label>
          <textarea
            id="share-model-message"
            v-model="message"
            :placeholder="$t('imageNavigation.attachMessage')"
          ></textarea>
        </div>
        <div class="share-by-email-button">
          <button :disabled="sending" @click="shareByEmail">
            {{ $t("shareModel.sendButton") }}
          </button>
        </div>
      </div>
    </div>

    <div class="share-by-link">
      <div class="share-by-link-title">
        <p>{{ $t("shareModel.copyLinkTitle") }}</p>
        <p>{{ $t("shareModel.copyLinkAccess") }}</p>
        <p class="share-by-link-validity">{{ $t("share.valid") }}</p>
      </div>
      <div class="copy-link-button">
        <button @click="copyLink">
          {{ $t("imageNavigation.copyLinkButton") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ElMessage } from "element-plus";
import API from "@/api/API";
import { ShareAccessLevel } from "@/constants/permissions";

const props = defineProps({
  projectId: { type: [Number, String], required: true },
  language: { type: String, default: "en" },
  isBobbie: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "shared"]);

const { t } = useI18n();

const accessOptions = [
  {
    value: ShareAccessLevel.MODEL_ONLY,
    labelKey: "shareModel.level1",
    hintKey: "shareModel.level1Hint",
  },
  {
    value: ShareAccessLevel.VIEW_ANNOTATIONS,
    labelKey: "shareModel.level2",
    hintKey: "shareModel.level2Hint",
  },
  {
    value: ShareAccessLevel.DRAFT_ANNOTATIONS,
    labelKey: "shareModel.level3",
    hintKey: "shareModel.level3Hint",
  },
];

// Matches what the single generic share used to hand out, so the familiar
// option stays the default and existing habits keep working.
const accessLevel = ref(ShareAccessLevel.DRAFT_ANNOTATIONS);
const email = ref("");
const message = ref("");
const sending = ref(false);

// The access level is signed into the token, so every level needs its own
// link. Fetched up front — and again whenever the level changes — so the
// clipboard write in copyLink() happens inside the click's user gesture
// rather than after a round-trip: Safari rejects the write once that
// gesture's transient activation has lapsed.
const shareLink = ref(null);
let shareLinkPromise = null;
// Each mint also burns a URL-shortener entry, so a level already fetched is
// reused when the sharer flips between the options rather than minted again.
const linkByLevel = new Map();

function fetchShareLink() {
  const requestedLevel = accessLevel.value;
  const cached = linkByLevel.get(requestedLevel);
  if (cached) {
    shareLink.value = cached;
    shareLinkPromise = Promise.resolve(cached);
    return shareLinkPromise;
  }

  shareLink.value = null;
  shareLinkPromise = API.projects
    .shareFastFusionSolarModel({
      language: props.language,
      project_id: props.projectId,
      is_bobbie: props.isBobbie,
      access_level: requestedLevel,
    })
    .then(({ data }) => {
      if (!data || !data.link) throw new Error("No share link in response");
      linkByLevel.set(requestedLevel, data.link);
      // Ignore a stale response if the level changed while it was in flight.
      if (accessLevel.value === requestedLevel) shareLink.value = data.link;
      return data.link;
    });
  shareLinkPromise.catch(() => {});
  return shareLinkPromise;
}

fetchShareLink();
watch(accessLevel, fetchShareLink);

function close() {
  emit("close");
}

function copyLink() {
  if (shareLink.value) {
    navigator.clipboard
      .writeText(shareLink.value)
      .then(() => emit("shared", { method: "copy", accessLevel: accessLevel.value }))
      .catch(() => ElMessage.error({ message: t("shareModel.linkFailed") }));
    return;
  }

  if (!shareLinkPromise) {
    ElMessage.error({ message: t("shareModel.linkFailed") });
    return;
  }

  // Still in flight (dialog just opened, or the level was just changed). A
  // wait before the write is unavoidable here, so fall back to the setTimeout
  // workaround as a best effort.
  shareLinkPromise
    .then((link) => {
      setTimeout(async () => {
        try {
          await navigator.clipboard.writeText(link);
          emit("shared", { method: "copy", accessLevel: accessLevel.value });
        } catch (err) {
          ElMessage.error({ message: t("shareModel.linkFailed") });
        }
      }, 0);
    })
    .catch(() => ElMessage.error({ message: t("shareModel.linkFailed") }));
}

// Accepts a few addresses at once, since a model usually goes to a whole
// project team rather than one person.
function parseRecipients(value) {
  return value
    .split(/[,;]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

async function shareByEmail() {
  const recipients = parseRecipients(email.value);
  if (recipients.length === 0) {
    ElMessage.error({ message: t("shareModel.emailRequired") });
    return;
  }

  sending.value = true;
  try {
    await API.projects.shareFastFusionSolarModelByEmail({
      language: props.language,
      project_id: props.projectId,
      is_bobbie: props.isBobbie,
      access_level: accessLevel.value,
      emails_list: recipients,
      message: message.value,
    });
    ElMessage.success({ message: t("shareModel.emailSent") });
    emit("shared", { method: "email", accessLevel: accessLevel.value });
    close();
  } catch (error) {
    ElMessage.error({ message: t("shareModel.emailFailed") });
  } finally {
    sending.value = false;
  }
}
</script>

<style lang="scss" scoped>
// Mirrors Gallery/Share.vue so the two share dialogs read as one feature; the
// height is auto here because the access chooser makes the body taller.
.share-model {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 656px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background-color: var(--color-dark-bg-secondary);
  color: var(--color-white);
  border-radius: 10px;
  // Above its backdrop, which is itself above every other layer — see
  // `.share-model-backdrop` in AirteamViewer.vue.
  z-index: 100001;

  .share-model-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 24px;
    border-bottom: 1px solid var(--color-border-separator);

    .share-model-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      p {
        font-size: 20px;
        font-weight: 600;
      }

      img {
        cursor: pointer;
      }
    }

    .share-model-access {
      width: 100%;
      margin-top: 16px;

      &-title {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
      }

      &-option {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        border: 1px solid var(--color-dark-bg-primary);
        border-radius: 10px;
        background-color: var(--color-dark-bg-primary);
        cursor: pointer;

        & + & {
          margin-top: 8px;
        }

        &--selected {
          border-color: var(--color-gree300);
        }

        input {
          margin-top: 2px;
          accent-color: var(--color-gree300);
          cursor: pointer;
        }

        &-text {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        &-label {
          font-size: 14px;
          font-weight: 600;
        }

        &-hint {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
        }
      }
    }

    .share-by-email {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;

      .share-by-email-container {
        width: 100%;
        margin: 16px 0;

        .share-by-email-title {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
          align-items: baseline;

          label {
            font-size: 14px;
            color: var(--color-white);
            font-weight: 600;
          }

          input {
            width: 100%;
            padding: 12px;
            border: 1px solid var(--color-dark-bg-primary);
            border-radius: 10px;
            background-color: var(--color-dark-bg-primary);
            color: var(--color-white);
            font-size: 14px;
            outline: none;

            &::placeholder {
              color: rgba(255, 255, 255, 0.5);
            }
          }
        }
      }

      .share-by-email-text {
        width: 100%;
        margin-bottom: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: baseline;

        label {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-white);
        }

        textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--color-dark-bg-primary);
          border-radius: 10px;
          background-color: var(--color-dark-bg-primary);
          color: var(--color-white);
          font-size: 14px;
          outline: none;
          resize: none;
          height: 110px;

          &::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }
        }
      }

      .share-by-email-button {
        width: 100%;
        display: flex;
        justify-content: flex-end;

        button {
          padding: 12px 16px;
          background-color: var(--color-gree300);
          border: none;
          border-radius: 5px;
          color: var(--color-black);
          font-size: 14px;
          cursor: pointer;
          outline: none;

          &:disabled {
            opacity: 0.6;
            cursor: default;
          }
        }
      }
    }
  }

  .share-by-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 24px;

    .share-by-link-title {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: baseline;

      p:first-child {
        font-size: 20px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.87);
      }

      p {
        font-size: 14px;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.87);
      }

      .share-by-link-validity {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
      }
    }

    .copy-link-button {
      button {
        background: none;
        border: none;
        color: var(--color-gree300);
        font-size: 14px;
        cursor: pointer;
        text-decoration: underline;
        outline: none;
        white-space: nowrap;
      }
    }
  }
}
</style>
